import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { api, setToken } from "../lib/api";
import type { User } from "../lib/types";
import { LangSwitch, useI18n, type MessageKey } from "../lib/i18n";
import { Button, ErrorBox } from "../components/ui";

const FEATURES: { icon: typeof HubOutlinedIcon; title: MessageKey; desc: MessageKey }[] = [
  { icon: HubOutlinedIcon, title: "login.f1t", desc: "login.f1d" },
  { icon: PublicOutlinedIcon, title: "login.f2t", desc: "login.f2d" },
  { icon: NotificationsActiveOutlinedIcon, title: "login.f3t", desc: "login.f3d" },
];

export function Login({ onSuccess }: { onSuccess: (user: User) => void }) {
  const { t } = useI18n();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.auth.login(username, password);
      setToken(res.accessToken);
      onSuccess(res.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
        bgcolor: "background.default",
      }}
    >
      {/* ── Panel trái: thương hiệu + điểm nổi bật (ẩn trên mobile) ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          position: "relative",
          overflow: "hidden",
          flexDirection: "column",
          justifyContent: "space-between",
          p: 6,
          color: "#fff",
          background:
            "linear-gradient(150deg, #1e3a8a 0%, #2563eb 48%, #4f46e5 100%)",
        }}
      >
        {/* Lớp ánh sáng nền tạo chiều sâu */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(620px 320px at 12% 8%, rgba(255,255,255,0.18), transparent 60%)," +
              "radial-gradient(560px 340px at 92% 96%, rgba(99,102,241,0.55), transparent 60%)",
          }}
        />
        {/* Lưới mờ trang trí */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.14,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage:
              "radial-gradient(600px 400px at 30% 20%, #000 30%, transparent 75%)",
          }}
        />

        <Stack direction="row" sx={{ alignItems: "center", gap: 1.5, position: "relative" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(255,255,255,0.16)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.25)",
            }}
          >
            <ShieldOutlinedIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
              {t("app.name")} {t("app.cms")}
            </Typography>
            <Typography sx={{ fontSize: 12.5, opacity: 0.8 }}>{t("app.tagline")}</Typography>
          </Box>
        </Stack>

        <Box sx={{ position: "relative", maxWidth: 460 }}>
          <Typography
            sx={{ fontSize: 34, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.02em", mb: 3 }}
          >
            {t("login.hero")}
          </Typography>
          <Stack sx={{ gap: 2.25 }}>
            {FEATURES.map(f => {
              const Icon = f.icon;
              return (
                <Stack key={f.title} direction="row" sx={{ gap: 1.75, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flex: "none",
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(255,255,255,0.14)",
                      border: "1px solid rgba(255,255,255,0.22)",
                    }}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{t(f.title)}</Typography>
                    <Typography sx={{ fontSize: 13, opacity: 0.82, lineHeight: 1.5 }}>
                      {t(f.desc)}
                    </Typography>
                  </Box>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Typography sx={{ position: "relative", fontSize: 12, opacity: 0.7 }}>
          © {new Date().getFullYear()} {t("app.name")}
        </Typography>
      </Box>

      {/* ── Panel phải: form đăng nhập ── */}
      <Box sx={{ display: "grid", placeItems: "center", p: { xs: 2.5, sm: 4 } }}>
        <Box
          component="form"
          onSubmit={submit}
          sx={{ width: "min(380px, 100%)", display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          {/* Logo nhỏ hiện trên mobile (khi panel trái bị ẩn) */}
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
          >
            <Stack direction="row" sx={{ alignItems: "center", gap: 1.25, minWidth: 0 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "10px",
                  flex: "none",
                  display: { xs: "grid", md: "none" },
                  placeItems: "center",
                  color: "#fff",
                  background: "linear-gradient(135deg, #3b82f6, #6366f1)",
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <LangSwitch fullWidth={false} />
            </Stack>
          </Stack>

          <Box>
            <Typography variant="h1" sx={{ fontSize: 24, fontWeight: 700, mb: 0.5 }}>
              {t("login.welcome")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("login.subtitle")}
            </Typography>
          </Box>

          {error && <ErrorBox message={error} />}

          <Stack sx={{ gap: 2 }}>
            <TextField
              label={t("login.username")}
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              slotProps={{ htmlInput: { minLength: 3 } }}
              required
              fullWidth
            />
            <TextField
              type="password"
              label={t("login.password")}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              slotProps={{ htmlInput: { minLength: 6 } }}
              required
              fullWidth
            />
          </Stack>

          <Button
            type="submit"
            variant="primary"
            loading={busy}
            style={{ paddingTop: 9, paddingBottom: 9, fontSize: 15 }}
          >
            {t("login.submit")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
